import { cn } from "@shared/lib/cn";

export interface PillTab<T extends string> {
  value: T;
  label: string;
}

interface PillTabsProps<T extends string> {
  options: ReadonlyArray<PillTab<T>>;
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
  className?: string;
  size?: "default" | "compact";
}

const pillSizes = {
  default: "px-4 py-1.5 text-body-sm font-medium",
  compact: "px-3 py-1.5 text-caption font-semibold uppercase tracking-wide",
} as const;

export function PillTabs<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
  size = "default",
}: PillTabsProps<T>): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        "flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(opt.value)}
            className={cn(
              "shrink-0 rounded-pill transition-colors",
              pillSizes[size],
              active
                ? "bg-primary-700 text-white"
                : "bg-card hairline text-ink-secondary",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
